<script setup>
import { useI18n } from 'vue-i18n';
import {
  markRaw, onBeforeMount, ref, unref, watch,
} from 'vue';
import { ErrorMessage, useForm } from 'vee-validate';
import { object, string } from 'yup';
import { RouterLink } from 'vue-router';
import { assoc, isEmpty } from 'ramda';
import { useGlobalState } from '@/store/user.store';
import Trans from '@/i18n/translation';
import GamebookLogo from '@/components/GamebookLogo.vue';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import { goToHomeScreenFor } from '@/services/ApiUtility';
import { useUserStateFromEndpoint } from '@/services/authentication';
import WithLoader from '@/components/WithLoader.vue';
import { trackEvent } from '@/services/analytics/googleAnalytics.js';
import { isLocalDevMode } from '../../config';

const { t } = useI18n();

const homeNavigation = Trans.i18nRoute({ name: 'home' });

const validationSchema = markRaw(
  object({
    email: string()
      .required(t('passwordValidator.emailRequired'))
      .email(t('passwordValidator.emailValid'))
      .label(t('loginView.email')),
    password: string()
      .required(t('passwordValidator.passwordRequired'))
      .label(t('loginView.password')),
  }),
);

const { defineField, handleSubmit } = useForm({
  validationSchema,
});

const [email, emailAttrs] = defineField('email');
const [password, passwordAttrs] = defineField('password');
// eslint-disable-next-line no-return-assign
watch(email, (newVal) => newVal !== newVal?.toLowerCase() && (email.value = newVal?.toLowerCase()));

const userMessage = ref('');
const pricingRoute = Trans.i18nRoute({ name: 'home', hash: '#pricing' });
const resetPasswordRoute = Trans.i18nRoute({ name: 'reset-password' });

const {
  getUserStateFromEndpoint: loginGetUserState, isLoading, userFriendlyErrorSalt, userFriendlyErrorLogin, // eslint-disable-line max-len
} = useUserStateFromEndpoint();

const clearUserMessages = () => {
  userMessage.value = '';
  userFriendlyErrorSalt.value = '';
  userFriendlyErrorLogin.value = '';
};
// eslint-disable-next-line max-len
const {
  currentEmployees, userState, currentCourses, isAuthenticated, resetUserState,
} = useGlobalState();

const doLogin = handleSubmit(async (values) => {
  clearUserMessages();
  trackEvent('login_attempt', {
    event_category: 'Login',
    event_label: 'Attempt login',
  });
  try {
    const userStateFromEndpoint = await loginGetUserState(values.email, values.password);
    if (isEmpty(userStateFromEndpoint)) {
      throw new Error('UserState is empty');
    }

    userState.value = assoc('isAuthenticated', true, userStateFromEndpoint);
    currentEmployees.value = userStateFromEndpoint.employees;
    currentCourses.set(userStateFromEndpoint.courses);

    trackEvent('login_success', {
      event_category: 'Login',
      event_label: 'Successful login',
    });
    await goToHomeScreenFor(unref(userState));
  } catch (e) {
    trackEvent('login_failed', {
      event_category: 'Login',
      event_label: 'Incorrect credentials',
    });
    userMessage.value = t('loginView.wrongCredentials');
    if (isLocalDevMode()) console.error(e);
  }
});

onBeforeMount(async () => {
  if (!isAuthenticated()) return;

  resetUserState();
  await goToHomeScreenFor(unref(userState));
});

</script>
<template>
  <main class="container mx-auto mt-8 min-w-80 w-1/4 base-gradient text-primary rounded-lg">
    <div
      class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8"
      v-if="!isAuthenticated()"
    >
      <div class="sm:mx-auto sm:w-full sm:max-w-sm">
        <RouterLink
          :to="homeNavigation"
          class="flex items-center justify-center mx-auto"
        >
          <GamebookLogo class="h-14 w-auto m-1.5 rounded-md" />
          <p class="text-sm text-primary">
            {{ $t('appTitle') }}
          </p>
        </RouterLink>
        <h2 class="mt-3 text-center text-lg font-bold leading-9 tracking-tight text-white">
          {{ $t('loginView.header') }}
        </h2>
      </div>

      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <InPlaceAlert
            :message="userFriendlyErrorSalt"
            @close-alert="clearUserMessages"
        />
        <InPlaceAlert
          :message="userMessage"
          type="error"
          @close-alert="clearUserMessages"
        />
        <form
          class="space-y-6"
          action="#"
          method="POST"
          @submit.prevent="doLogin"
        >
          <div>
            <label
              for="email"
              class="block text-sm font-medium leading-6 text-white"
            >{{
              $t('loginView.email')
            }}</label>
            <div class="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                :placeholder="$t('loginView.emailPlaceholder')"
                autocomplete="email"
                required="required"
                v-model="email"
                v-bind="emailAttrs"
                class="block w-full input-transparent-text"
              >
              <ErrorMessage
                name="email"
                class="px-2 mt-2 text-sm text-red-400 dark:text-red-400"
                as="p"
              />
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between">
              <label
                for="password"
                class="block text-sm font-medium leading-6 text-white"
              >{{
                $t('loginView.password')
              }}</label>
            </div>
            <div class="mt-2">
              <input
                id="password"
                name="password"
                type="password"
                autocomplete="current-password"
                required="required"
                v-model="password"
                v-bind="passwordAttrs"
                class="block w-full input-transparent-text"
              >
              <ErrorMessage
                name="password"
                class="px-2 mt-2 text-sm text-red-400 dark:text-red-400"
                as="p"
              />
            </div>

            <div class="flex items-center justify-end pt-1.5">
              <div class="text-sm">
                <RouterLink
                  :to="resetPasswordRoute"
                  class="font-semibold leading-6 text-secondary hover:text-highlight"
                >
                  {{ $t('loginView.forgotPassword') }}
                </RouterLink>
              </div>
            </div>
          </div>

          <div>
            <WithLoader :is-loading="isLoading">
              <button
                type="submit"
                class="flex w-full justify-center btn-cta"
              >
                {{ $t('logIn') }}
              </button>
            </WithLoader>
          </div>
        </form>

        <p class="mt-3 text-center text-sm">
          {{ $t('loginView.noAccount') }}
          {{ ' ' }}
          <RouterLink
            :to="pricingRoute"
            class="font-semibold leading-6 text-secondary hover:text-highlight"
          >
            {{ $t('loginView.signUp') }}
          </RouterLink>
        </p>
      </div>
    </div>
  </main>
</template>
